import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faUsers } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { useUpdateMembership } from 'src/features/userManagement/api/useUpdateMembership.ts';
import { userManagementMessages } from 'src/features/userManagement/utils';
import { useParking } from 'src/providers/ParkingProvider.tsx';
import SquareIcon from 'src/registry/components/blocks/SquareIcon.tsx';
import { useToast } from 'src/registry/hooks/UseToast.ts';
import { Button } from 'src/registry/ui/Button.tsx';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog.tsx';
import { type GeneralSmartParkError } from 'src/types';
import { generalErrorMessages, generalMessages } from 'src/utils/messages.ts';

import type { Membership } from 'src/features/userManagement/types';

type UserStatusChangeModalProps = {
    isUserStatusChangeModalOpen: boolean;
    closeUserStatusChangeModal: (isOpen: boolean) => void;
    selectedMember: Membership;
    statusAction: 'activate' | 'deactivate';
    refetch: () => void;
};

const UserStatusChangeModal = ({
    isUserStatusChangeModalOpen,
    closeUserStatusChangeModal,
    selectedMember,
    statusAction,
    refetch,
}: UserStatusChangeModalProps) => {
    const { formatMessage } = useIntl();
    const { toast } = useToast();
    const { parkingId } = useParking();

    const modalTitle = statusAction === 'deactivate' ? generalMessages.deactivate : generalMessages.activate;
    const descriptionStatus =
        statusAction === 'activate' ? userManagementMessages.activateUser : userManagementMessages.deactivateUser;

    const toastTitle =
        statusAction === 'activate' ? userManagementMessages.userActivated : userManagementMessages.userDeactivated;

    const userNameOrEmail = selectedMember.user.name ? selectedMember.user.name : selectedMember.user.email;

    const onErrorHandle = (error: GeneralSmartParkError) => {
        const errorData = error.response?.data;
        const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
        const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

        toast({
            title: errorCode,
            description: errorMessage,
            variant: 'destructive',
        });
    };
    const { mutate: updateMembership, isPending: isPendingUpdate } = useUpdateMembership({
        onSuccess: () => {
            refetch();
            // onOpenChange(false);
            toast({
                title: formatMessage(userManagementMessages.userStatusChanged, {
                    status: formatMessage(toastTitle).toLowerCase(),
                }),
                description: formatMessage(userManagementMessages.userStatusChangedDescription, {
                    name: userNameOrEmail,
                    status: formatMessage(toastTitle).toLowerCase(),
                }),
            });
        },
        onError: (error) => onErrorHandle(error),
    });

    const onSubmit = () => {
        updateMembership({
            parkingId: parkingId,
            membershipId: selectedMember.id,
            status: statusAction === 'activate' ? 'active' : 'inactive',
        });
    };

    return (
        <Dialog open={isUserStatusChangeModalOpen} onOpenChange={closeUserStatusChangeModal}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <SquareIcon icon={faUsers} />
                        <DialogTitle>{`${formatMessage(modalTitle)} ${formatMessage(userManagementMessages.user).toLowerCase()}`}</DialogTitle>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        {formatMessage(userManagementMessages.changeMembershipStatusConfirmation, {
                            status: formatMessage(descriptionStatus).toLowerCase(),
                            name: userNameOrEmail,
                        })}
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant={statusAction === 'deactivate' ? 'destructiveLight' : 'default'}
                        onClick={() => {
                            if (selectedMember) {
                                return (onSubmit(), closeUserStatusChangeModal(!isUserStatusChangeModalOpen));
                            }
                        }}
                        className="flex-1"
                    >
                        {isPendingUpdate ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        <FormattedMessage {...generalMessages.saveChanges} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UserStatusChangeModal;
